import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { GraduationCap, Award, ArrowRight, UploadCloud, Sparkles } from "lucide-react";

export default function StudentProjectUpload() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    type: user?.student_type === "graduate" ? "thesis" : "project",
    title: "", abstract: "", domain: "Multidisciplinary",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const r = await api.get("/students/uploads/mine");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.abstract.length < 50) { toast.error("Abstract must be at least 50 chars"); return; }
    setSubmitting(true);
    try {
      await api.post("/students/upload", form);
      toast.success("Submitted — admin will review for publication feasibility.");
      setForm({ ...form, title: "", abstract: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const isGrad = user?.student_type === "graduate";

  // Route guard — only students (or admin previewing) can access this page
  if (user && !["student", "admin"].includes(user.role)) {
    return <Navigate to="/dashboard/ideas/new" replace />;
  }

  return (
    <div data-testid="student-upload-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{isGrad ? "THESIS UPLOAD" : "PROJECT UPLOAD"}</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Convert your <em className="italic text-kb-primary">{isGrad ? "thesis" : "project"}</em> into an impactful research paper.
      </h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Upload your {isGrad ? "thesis" : "final-year project"} — our admin team reviews for publication feasibility, and approved works enter the full publication workflow with a domain mentor.
      </p>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <form onSubmit={submit} className="lg:col-span-7 kb-card p-6 space-y-4" data-testid="student-upload-form">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-amber-700">FREE FOR PARTNER INSTITUTION STUDENTS</div>
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Type</label>
            <div className="mt-1.5 flex gap-2">
              {["project", "thesis"].map((t) => (
                <button type="button" key={t} onClick={() => setForm({ ...form, type: t })}
                  className={`px-3 py-2 rounded-sm border text-sm capitalize ${form.type === t ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200"}`}
                  data-testid={`upload-type-${t}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Title *</label>
            <input required className="kb-input mt-1.5" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="upload-title-input" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Abstract *</label>
            <textarea required rows={5} className="kb-input mt-1.5 resize-none" value={form.abstract}
              onChange={(e) => setForm({ ...form, abstract: e.target.value })} data-testid="upload-abstract-input" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Domain</label>
            <select className="kb-input mt-1.5" value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="upload-domain-select">
              {["Computer Science", "AI/ML", "Data Science", "Public Health", "Medical Sciences",
                "Civil Engineering", "Climate Science", "Environment", "Economics",
                "Education", "Social Sciences", "Multidisciplinary"].map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <label className="block border-2 border-dashed border-slate-300 rounded-sm p-5 text-center bg-slate-50 cursor-pointer">
            <UploadCloud className="w-5 h-5 text-kb-ink-muted mx-auto" />
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2">ATTACH PDF/DOCX (optional)</div>
            <input type="file" className="hidden" />
          </label>
          <button type="submit" disabled={submitting} className="kb-btn-primary" data-testid="upload-submit-btn">
            {submitting ? "Submitting…" : (<>Submit for review <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>

        <div className="lg:col-span-5 space-y-3">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">YOUR SUBMISSIONS</div>
          {items.length === 0 ? <div className="kb-card p-6 text-sm text-kb-ink-muted">No submissions yet.</div> :
            items.map((s) => (
              <div key={s.id} className="kb-card p-5" data-testid={`upload-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{s.id} · {s.type} · {s.student_type}</div>
                  <span className={`kb-mono text-[10px] uppercase px-2 py-1 rounded-sm ${
                    s.status === "approved" ? "text-emerald-700 bg-emerald-50 border border-emerald-200" :
                    s.status === "rejected" ? "text-rose-700 bg-rose-50 border border-rose-200" :
                    "text-amber-700 bg-amber-50 border border-amber-200"
                  }`}>{s.status}</span>
                </div>
                <div className="font-serif text-base text-kb-ink mt-2 leading-tight">{s.title}</div>
                {s.converted_idea_id && (
                  <Link to={`/dashboard/ideas/${s.converted_idea_id}`} className="kb-mono text-[10px] text-emerald-700 mt-2 inline-block">
                    → CONVERTED TO IDEA {s.converted_idea_id}
                  </Link>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
